/**
 * UC-20: StartScenario
 * Thin wrapper validating scenarioId / industryCategory and returning a StartScenarioResult DTO.
 */
import { SCENARIOS, TUTORIAL_CAMPAIGN } from '../../../constants';
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import type { IIndustryCatalog } from '../../domain/ports/IIndustryCatalog';

export interface StartScenarioInput {
  aiEnabled: boolean;
  scenarioId?: string;
  industryCategory?: string;
}

export interface StartScenarioResult {
  success: boolean;
  mode: 'scenario' | 'sandbox';
  aiEnabled: boolean;
  scenarioId?: string;
  industryCategory?: string;
  errors: string[];
}

export interface StartScenarioDependencies {
  logger?: IAppLogger;
  catalog: IIndustryCatalog;
}

const KNOWN_SCENARIO_IDS = new Set([
  ...SCENARIOS.map((s) => s.id),
  ...TUTORIAL_CAMPAIGN.map((s) => s.id),
]);

export class StartScenarioUseCase {
  constructor(private readonly deps: StartScenarioDependencies = {}) {}

  execute(input: StartScenarioInput): StartScenarioResult {
    const errors: string[] = [];

    if (input.scenarioId) {
      if (!KNOWN_SCENARIO_IDS.has(input.scenarioId)) {
        errors.push(`Unknown scenarioId: ${input.scenarioId}`);
      }
      const result: StartScenarioResult = {
        success: errors.length === 0,
        mode: 'scenario',
        aiEnabled: input.aiEnabled,
        scenarioId: input.scenarioId,
        errors,
      };
      this.log(result);
      return result;
    }

    const industry = input.industryCategory ?? 'sandbox';
    if (!this.deps.catalog.isKnownIndustry(industry)) {
      errors.push(`Unknown industryCategory: ${industry}`);
    }

    const result: StartScenarioResult = {
      success: errors.length === 0,
      mode: 'sandbox',
      aiEnabled: input.aiEnabled,
      industryCategory: industry,
      errors,
    };
    this.log(result);
    return result;
  }

  private log(result: StartScenarioResult): void {
    this.deps.logger?.log(
      result.success ? 'info' : 'warn',
      'scenario.start',
      {
        mode: result.mode,
        scenarioId: result.scenarioId,
        industryCategory: result.industryCategory,
        aiEnabled: result.aiEnabled,
        errors: result.errors,
      },
    );
  }
}